import { useDispatch, useSelector } from 'react-redux';
import { Box, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Footer, Confirm } from './Footer';
import { useBenefitCategoryQuery } from '../../queries';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgPlanYearId,
} from '../../../theSource/features/Home/components/EnrollmentBanner/services';
import {
  EnrollmentContext,
  PlanYear,
  useConfigurationQuery,
  useEnrollmentContextQuery,
} from '../../../theSource/queries';
import { BenefitDto } from 'enrollment/queries/useBenefitCategoryQuery/types';
import { BenefitCategory } from './components';
import { PerPayPeriodHeader } from 'features/MyBenefits/components';
import {
  selectPerPayPeriodCost,
  setPerPayPeriodCost,
} from 'enrollment/components/Header/components/EnrollmentNavigation/services';
import { getPlanYears } from 'enrollment/utils/common/Utils';
import { GenericGroupingContainer } from '../Shared/PreviewContainers/GenericGroupingContainer';
import { queryKeyGenerator } from 'theSource/utils';
import { setBenefitData } from '../../services/Benefit/slice';
import {
  DefinedBenefitContributionBanner,
  PageContentHeaderDisplay,
} from 'enrollment/components';
import { ThemedHeader } from 'components/ThemedHeader';
import { benefitCategorySelector } from 'enrollment/services/BenefitCategories/selectors';
import {
  organizeBundledBenefitCategoryBenefits,
  useBundledBenefitCategories,
} from 'enrollment/utils/bundledBenefitsUtils';
import { mapCategoriesData } from '../Benefits/BenefitsPreview/utils';

export const getErCost = (benefits: BenefitDto[]) => {
  let cost = 0;
  benefits.forEach((benefit) => {
    cost += Number(
      benefit.coverage?.req_org_premium ? benefit.coverage?.req_org_premium : 0
    );
  });
  return cost;
};

export const Review = () => {
  const { t } = useTranslation('pages.enrollment.review');
  let benefits = undefined;
  let erCost = 0;
  const isScreenSmall = window.innerWidth < 744;
  const enrollmentContextType = useSelector(selectEnrollmentContext);
  let orgPlanYearContextId = useSelector(selectEnrollmentOrgPlanYearId);
  orgPlanYearContextId =
    enrollmentContextType === 'life_event' ? orgPlanYearContextId : 0;
  const { configuration } = useConfigurationQuery();
  const dispatch = useDispatch();

  const lumosApiHost = configuration?.apiHost;
  const {
    data: benefitsData,
    isFetching,
    isLoading,
  } = useBenefitCategoryQuery({
    key: queryKeyGenerator(
      `benefits-${enrollmentContextType}`,
      orgPlanYearContextId
    ),
    enrollmentContextType: enrollmentContextType,
    orgPlanYearId: orgPlanYearContextId,
    enabled: enrollmentContextType !== '',
    lumosApiHost: String(lumosApiHost),
    getParamCategory: false,
  });
  let eePPPCost = useSelector(selectPerPayPeriodCost);

  const {
    enrollmentContext,
    isFetchingEnrollmentContext,
    isLoadingEnrollmentContext,
  } = useEnrollmentContextQuery({
    enabled: true,
    apiHost: String(lumosApiHost),
  });

  let planYears: PlanYear[] = [];
  if (enrollmentContext) {
    const contexts = enrollmentContext?.data as EnrollmentContext[];
    const activeContextIndex = contexts.findIndex(
      (context) => context.enrollment_type_lookup_code === enrollmentContextType
    );
    if (activeContextIndex >= 0) {
      planYears = getPlanYears(
        contexts,
        activeContextIndex,
      );
    }
  }

  let benefitCategories = useSelector(benefitCategorySelector);
  if (!benefitCategories || benefitCategories.length === 0) {
    benefitCategories = planYears && mapCategoriesData(planYears);
  }
  const bundledBenefitCategories = useBundledBenefitCategories();

  const getReviewBenefits = (
    benefits: BenefitDto[] | undefined,
    category: string
  ): BenefitDto[] | undefined => {
    const bundledCategories = bundledBenefitCategories.flatMap(
      (cat) => cat.lookup_code
    );
    if (bundledCategories.includes(category) && benefits) {
      return organizeBundledBenefitCategoryBenefits(
        benefits,
        category as 'chosenBundle' | 'otherBundle',
        true,
        benefitsData?.data?.bundled_org_benefit_ids
      );
    }
    return (
      benefits &&
      benefits.filter(
        (benefit) => benefit.benefit.org_benefit_category === category
      )
    );
  };

  const loading =
    isLoading ||
    isLoadingEnrollmentContext ||
    isFetching ||
    isFetchingEnrollmentContext;
  if (loading) {
    benefits = undefined;
    erCost = 0;
  } else {
    benefits = benefitsData?.data?.benefits;
    eePPPCost = Number(benefitsData?.data?.cart_total || 0);
    if (benefitsData) {
      dispatch(setBenefitData({ benefitsData: benefitsData }));
    }
    dispatch(setPerPayPeriodCost(eePPPCost));
    erCost = benefits ? getErCost(benefits) : 0;
  }

  return (
    <Box data-testid="review-container" id="review-container">
      <div className="mt-md-5 mt-lg-4 p-md-0 pb-4 pt-4_5 pt-md-4_5 px-4 pt-lg-2">
        <DefinedBenefitContributionBanner benefits={benefitsData} />
        <ThemedHeader>{t('title')}</ThemedHeader>
        <h2 className="mb-0 mb-md-4 mt-md-n2">{t('subtitle')}</h2>
        {planYears && planYears.length > 0 && (
          <PageContentHeaderDisplay
            pageContentLookupCode="enroll_confirm"
            displayPageHeader={false}
            orgPlanYearId={planYears[0].id}
          />
        )}
      </div>

      {!loading ? (
        isScreenSmall ? (
          <div className="card">
            <PerPayPeriodHeader
              totalSubPremium={eePPPCost}
              totalOrgPremium={erCost}
              additionalClasses="flex-column-reverse w-100"
            />
          </div>
        ) : (
          <div className="card">
            <PerPayPeriodHeader
              totalSubPremium={eePPPCost}
              totalOrgPremium={erCost}
              additionalClasses="border-bottom-0 p-0"
            />
          </div>
        )
      ) : (
        <div className="card">
          <Skeleton
            width={820}
            height={200}
            role="status"
            aria-label="Loading..."
          />
        </div>
      )}

      {loading && (
        <GenericGroupingContainer
          id="benefit-loading"
          groupName=""
          loading={true}
        >
          Loading...
        </GenericGroupingContainer>
      )}

      {benefitCategories.map((category, index) => (
        <BenefitCategory
          key={index}
          coverages={getReviewBenefits(benefits, category.lookup_code)}
          category={category.name}
          contextLookup={enrollmentContextType}
        ></BenefitCategory>
      ))}
      <Footer></Footer>
      <Confirm></Confirm>
    </Box>
  );
};
// written with ai assistance
